import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import Utils, { GetHelper } from 'src/app/helpers/utils';
import { StorageService } from 'src/app/services/storage/storage.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-companies-user-details',
  templateUrl: './dashboard-companies-user-details.component.html',
  styleUrls: ['./dashboard-companies-user-details.component.scss'],
})
export class DashboardCompaniesUserDetailsComponent
  implements OnInit, OnDestroy
{
  permissionObj = {
    canReset: true,
    canChange: true,
    canInactive: true,
  };

  canViewHistory = false;
  showResetModal = false;
  private destroy$ = new Subject<void>();

  // Propriedades para o warning modal
  showStatusModal = false;
  isActive: boolean = false;

  profilesData: CompanyProfileListValues[] = [];
  usersData: CompanyUsersListValues[] = [];
  historyData: any[] = [];

  filterForm = this.formBuilder.group({
    name: [null, []],
    department: [null, []],
    email: [null, []],
    document: [null, []],
    start_date: [null, []],
    end_date: [null, []],
    users_type: [null, []],
  });

  form = this.formBuilder.group({
    name: [null, [Validators.required]],
    department: [null, [Validators.required]],
    email: [null, [Validators.required]],
    profile: [null, [Validators.required]],
    document: [null, [Validators.required, Validators.maxLength(14)]],
  });

  usersLoading = true;
  historyLoading = true;
  hasFilters = false;
  showFilterModal = false;
  showUserModal = false;
  showProfilesFilterModal = false;
  isMaster = false;
  isSimple = false;
  acitveRegisterMasterUser = false;
  data: any = null;

  submiting = false;
  departmentOptions: SelectItem[] = [];
  profileOptions: SelectItem[] = [];

  historyOffset: number = 0;
  historyCount: number = 0;
  loading = true;
  sendEmail = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private storageService: StorageService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    if (
      !this.authService.verifyPermission(['de_can_view_company_users_details'])
    ) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.'
      );
      this.router.navigateByUrl('/app/dashboard');
    }

    this.canViewHistory = this.authService.verifyPermission(
      'de_can_view_company_users_history'
    );

    this.acitveRegisterMasterUser =
      this.storageService.getPermissionType() === 'master';

    this.setBreadcrumb();
    this.setPermissions();

    // Escuta mudanças nos parâmetros da rota
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      // Recarrega os dados quando o ID do usuário muda
      this.getData();
    });

    this.filterForm.valueChanges.subscribe((values) => {
      this.hasFilters = Object.values(values).some(
        (value) => value !== null && value !== ''
      );
    });

    this.form.controls.department.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        this.form.controls.profile.setValue(null);
        if (value) {
          this.getProfiles();
        } else {
          this.profileOptions = [];
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getData() {
    await this.getUserData();
    await Promise.all([this.getHistoryData(), this.getDepartments()]);
  }

  async getDepartments() {
    try {
      const hasCompany =
        this.data.company.length > 0 &&
        this.data.company[0].company_id !== undefined;

      const res = await this.api.get({
        route: 'api/v2/company_department/',
        token: true,
        params: {
          company: hasCompany ? this.data.company[0].company_id : 0,
        },
      });

      this.departmentOptions = res.map((item) => ({
        label: item.department,
        value: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  async getProfiles() {
    try {
      const id = this.route.snapshot.paramMap.get('id');

      let params: any = {
        tab: 'user_profile',
      };

      if (this.isMaster) {
        params = {
          ...params,
          master_user: true,
        };
      }

      if (this.isSimple) {
        params = {
          ...params,
          default_user: true,
        };
      }

      if (this.form.value.department) {
        params = {
          ...params,
          department: this.form.value.department,
        };
      }

      if (
        this.form.value.department === null ||
        this.form.value.department === undefined
      )
        return;

      const res = await this.api.get({
        route: `api/v3/companies/${id}/`,
        params,
        token: true,
      });

      // V3 sempre retorna paginação
      const profiles = res.results || res;
      this.profileOptions = profiles.map((item) => ({
        label: item.description,
        value: item.id,
        extra: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  async getUserData() {
    try {
      const userId = this.route.snapshot.params.userId;

      const res = await this.api.get({
        route: `api/v2/user_group_user/${userId}`,
        token: true,
      });

      this.data = res;
      this.setBreadcrumb(); // Atualizar breadcrumb após carregar os dados
    } catch (error) {
      console.log(error);
    }
    this.loading = false;
  }

  async getHistoryData(page: Number = 1) {
    try {
      const { count, results, offset } = await this.api.get({
        route: `api/v2/permission_history`,
        token: true,
        params: {
          user: this.data.register,
          page: page,
        },
      });

      this.historyData = results;
      this.historyOffset = offset;
      this.historyCount = count;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }

    this.historyLoading = false;
  }

  changePageData(page: Number) {
    this.historyLoading = true;
    this.getHistoryData(page);
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_company_users`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  handleCloseModal() {
    this.showProfilesFilterModal = false;
    this.showFilterModal = false;
    this.showUserModal = false;
    this.form.reset();
  }

  async toggleUserModal() {
    this.showUserModal = !this.showUserModal;

    if (this.showUserModal) {
      this.form.patchValue({
        name: this.data.user,
        department: Array.isArray(this.data.department_id)
          ? this.data.department_id[0]
          : this.data.department_id,
        email: this.data.email,
        document: this.data.document_number,
      });
      //   this.handleChangeValue(this.data.type === 'Comum' ? 'simple' : 'master');
      this.isMaster = this.data.type === 'Comum' ? false : true;
      this.isSimple = this.data.type === 'Comum' ? true : false;
      await this.getProfiles();

      this.form.controls.profile.setValue(
        this.data.profiles.length > 0 ? this.data.profiles[0] : null
      );

      this.form.controls.profile.setValidators(
        this.isSimple ? [Validators.required] : []
      );

      this.form.controls.profile.setValue(null);
      this.form.controls.profile.updateValueAndValidity();
    }
  }

  toggleResetModal() {
    this.showResetModal = !this.showResetModal;
  }

  async resetPassword() {
    try {
      this.sendEmail = true;

      await this.api.post({
        route: 'request-token/',
        body: {
          username: this.data.document_number,
          channel: 'email',
          reset: true,
        },
      });

      this.toast.show(
        'info',
        'Sucesso',
        'E-mail de recuperação enviado com sucesso.'
      );

      this.showResetModal = false;
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao enviar o e-mail de recuperação.'
      );
      console.log(error);
    }

    this.sendEmail = false;
  }

  async submit() {
    this.submiting = true;
    try {
      const userId = this.route.snapshot.params.userId;
      const valuesHelper = new GetHelper(this.form.value);

      const last_name = valuesHelper.get('name').split(' ').slice(1).join(' ');
      const collaboratorPayload: any = {
        email: valuesHelper.get('email'),
        first_name: valuesHelper.get('name').split(' ')[0],
        last_name,
        username: Utils.onlyNumbers(valuesHelper.get('document')),
        is_approver: this.isMaster,
        department: valuesHelper.get('department'),
        type_dispay:
          valuesHelper.get('document').length === 14
            ? 'Pessoa Jurídica'
            : 'Pessoa Física',
      };

      // Adiciona permission_profile apenas quando NÃO for master
      if (!this.isMaster && valuesHelper.get('profile')) {
        collaboratorPayload.permission_profile = valuesHelper.get('profile');
      }

      const payload = {
        username: Utils.onlyNumbers(valuesHelper.get('document')),
        simple_user: !this.isMaster,
        manager_user: this.isMaster,
        company: [this.route.snapshot.params.id],
        permission_group: this.route.snapshot.params.groupId
          ? this.isMaster
            ? []
            : [Number(this.route.snapshot.params.groupId)]
          : [],
        profiles: this.isMaster ? [] : [valuesHelper.get('profile')],
        department: this.isMaster ? [] : [valuesHelper.get('department')],
      };

      await this.api.post({
        route: 'api/collaborator/',
        token: true,
        body: collaboratorPayload,
      });

      await this.api.put({
        route: `api/v2/user_group_user/${userId}/`,
        token: true,
        body: payload,
      });

      this.toast.show('info', 'Sucesso', 'Usuário atualizado com sucesso.');

      //   await this.api.post({
      //     route: 'request-token/',
      //     body: {
      //       username: payload.username,
      //       channel: 'email',
      //       reset: true,
      //     },
      //   });
      this.showUserModal = false;
      this.form.reset();
      this.loading = true;
      this.getUserData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao salvar os dados.');
    }
    this.submiting = false;
  }

  getDisabled() {
    return this.form.invalid || this.submiting;
  }

  handleChangeValue(type: string) {
    if (type === 'master') {
      if (this.isMaster) {
        this.isMaster = false;
        this.isSimple = true;
      } else {
        this.isMaster = true;
        this.isSimple = false;
      }
    }

    if (type === 'simple') {
      if (this.isSimple) {
        this.isSimple = false;
        this.isMaster = true;
      } else {
        this.isSimple = true;
        this.isMaster = false;
      }
    }

    this.form.controls.profile.setValidators(
      this.isSimple ? [Validators.required] : []
    );

    this.form.controls.profile.setValue(null);
    this.form.controls.profile.updateValueAndValidity();
    this.getProfiles();
  }

  toggleUserStatus() {
    this.isActive = this.data.status === 'Ativo';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      const res = await this.api.patch({
        route: `api/v2/user_group_user/${this.data.id}/`,
        token: true,
        body: {
          status: this.data.status === 'Ativo' ? 'Inativo' : 'Ativo',
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status da empresa.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar usuário' : 'Ativar usuário';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse usuário? Todas as permissões vinculadas a esse usuário também serão afetadas.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} usuário`;
  }

  formatDocument(type, document: string) {
    if (type === 'cpf') {
      return (
        'CPF: ' +
        document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
      );
    }

    return (
      'CNPJ: ' +
      document.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    );
  }

  setBreadcrumb(): void {
    const canViewCompanies = this.authService.verifyPermission(
      'de_can_view_companies'
    );
    const companyId = this.route.snapshot.params.id;
    const userId = this.route.snapshot.params.userId;
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Empresas',
        url: '/app/permissions/companies',
        clickable: canViewCompanies,
      },
      {
        label: 'Detalhes da empresa',
        url: `/app/permissions/companies/details/${companyId}`,
        clickable: canViewCompanies,
      },
      {
        label: this.data?.name || 'Detalhes de usuário',
        url: `/app/permissions/companies/${companyId}/user/details/${userId}`,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
